import { createContext, useContext, useEffect, useLayoutEffect } from "react";

import { analytics } from "./analyticsManager";
import { useUserContext } from "../../contexts/UserProvider";
import { useRouter } from "next/router";
import { useTestMode } from "../../contexts/TestModeProvider";

type AnalyticsContextType = {
  analytics: typeof analytics;
};

const AnalyticsContext = createContext<AnalyticsContextType>(
  {} as AnalyticsContextType
);

type AnalyticsProviderProps = Omit<AnalyticsContextType, "analytics"> & {
  children: React.ReactNode;
};

export default function AnalyticsProvider({
  children,
}: AnalyticsProviderProps) {
  const { currentUser } = useUserContext();
  const { isTestMode } = useTestMode();
  const router = useRouter();

  useEffect(() => {
    if (!currentUser) return;

    const { name, email, organization_name, persona } = currentUser;
    analytics.identify({
      name,
      email,
      organization_name,
      persona,
    });
  }, [currentUser]);

  useEffect(() => {
    analytics.track("page_view", { page_name: router.pathname });
  }, [router.pathname]);

  useLayoutEffect(() => {
    analytics.register({ is_test_mode: isTestMode });
  }, [isTestMode]);

  return (
    <AnalyticsContext.Provider value={{ analytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    console.warn("useAnalytics must be used inside <AnalyticsProvider />");
  }

  return context;
};
